import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CanAccess } from "ra-core";
import {
  CircleStop,
  CloudCog,
  ExternalLink,
  Film,
  FlaskConical,
  LoaderCircle,
  Sparkles,
  TriangleAlert,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { flattenPricing } from "../ai-settings/pricing";
import {
  useI2vRegistry,
  type Capabilities,
  type ModelVariant,
  type Provider,
} from "../ai-settings/registry";
import {
  cancelLabJob,
  createLabJob,
  getLabJob,
  isActiveLabStatus,
  listLabJobs,
  uploadLabImage,
  type LabJob,
  type LabProviderOptions,
} from "./api";
import { FrameInput } from "./FrameInput";
import { labBudgetExceeded } from "./errors";
import { LabBudgetAlert } from "./LabBudgetAlert";
import { LabHistory } from "./LabHistory";
import { TestEchoLauncherCard } from "./TestEchoLauncherCard";
import { LabRunRecipe } from "./LabRunRecipe";
import {
  concreteKlingProviderLabel,
  resolveLabProviderIdentity,
} from "./providerIdentity";
import { ReadinessDiagnostics } from "./ReadinessDiagnostics";
import { ReferenceImagesInput } from "./ReferenceImagesInput";
import { useObjectUrl } from "./useObjectUrl";

// Labs ships in the entry chunk; the verified-likeness card loads on demand so
// it never counts against the entry bundle budget (scripts/bundle-budget.json).
const LikenessCard = lazy(() =>
  import("./LikenessCard").then((module) => ({
    default: module.LikenessCard,
  })),
);

const durationOptions = (provider: Provider, caps: Capabilities) => {
  if (provider.labs?.duration_seconds.length) {
    return provider.labs.duration_seconds;
  }
  if (caps.supported_durations_seconds.length) {
    return caps.supported_durations_seconds;
  }
  const min = caps.min_duration_seconds ?? 1;
  const max = caps.max_duration_seconds ?? 15;
  return Array.from(new Set([min, Math.min(Math.max(5, min), max), max]));
};

const firstOption = (values: string[], fallback = "") => values[0] ?? fallback;

const operationCapabilities = (
  model: ModelVariant | undefined,
  fallback: Capabilities | undefined,
) => {
  if (!fallback) return undefined;
  const contract = model?.operations?.find(
    (operation) => operation.id === "image_to_video",
  )?.input_contract;
  if (!contract) return fallback;
  const duration = contract.duration_seconds;
  const features = contract.features;
  return {
    ...fallback,
    supported_durations_seconds:
      duration?.values ?? fallback.supported_durations_seconds,
    min_duration_seconds: duration?.minimum ?? fallback.min_duration_seconds,
    max_duration_seconds: duration?.maximum ?? fallback.max_duration_seconds,
    supported_resolutions:
      contract.resolutions ?? fallback.supported_resolutions,
    aspect_ratio_control:
      contract.aspect_ratio?.behavior ?? fallback.aspect_ratio_control,
    supported_aspect_ratios:
      contract.aspect_ratio?.values ?? fallback.supported_aspect_ratios,
    supports_first_last_frame:
      features?.last_frame ?? fallback.supports_first_last_frame,
    supports_reference_images:
      features?.reference_images ?? fallback.supports_reference_images,
    max_reference_images:
      features?.reference_image_maximum ?? fallback.max_reference_images,
    supports_negative_prompt:
      features?.negative_prompt ?? fallback.supports_negative_prompt,
    supports_camera_control:
      features?.camera_control ?? fallback.supports_camera_control,
    supports_native_audio:
      features?.native_audio ?? fallback.supports_native_audio,
    supports_seed: features?.seed ?? fallback.supports_seed,
  } satisfies Capabilities;
};

const providerOptions = (
  providerId: string,
  workflowId: string,
  loraId: string,
  loraStrength: string,
): LabProviderOptions | undefined => {
  if (providerId === "kling_piapi") return { provider_id: "kling_piapi" };
  if (providerId === "kling_direct") return { provider_id: "kling_direct" };
  if (providerId === "h3_comfy") return { provider_id: "h3_comfy" };
  if (providerId === "ltx_comfy") {
    return {
      provider_id: "ltx_comfy",
      workflow_id: workflowId,
      lora_id: loraId,
      lora_strength: Number(loraStrength),
    };
  }
  return undefined;
};

const constrainedResolutions = (
  model: ModelVariant | undefined,
  fallback: string[],
  generateAudio: boolean,
) => {
  if (!generateAudio) return fallback;
  const constraints = model?.operations?.find(
    (operation) => operation.id === "image_to_video",
  )?.input_contract.constraints;
  for (const constraint of constraints ?? []) {
    const condition = constraint.if as
      | { field?: string; equals?: unknown }
      | undefined;
    const effect = constraint.then as
      | { field?: string; allowed_values?: unknown }
      | undefined;
    if (
      constraint.rule === "conditional" &&
      condition?.field === "generate_audio" &&
      condition.equals === true &&
      effect?.field === "resolution" &&
      Array.isArray(effect.allowed_values)
    ) {
      const allowed = effect.allowed_values.filter(
        (value): value is string => typeof value === "string",
      );
      return fallback.filter((value) => allowed.includes(value));
    }
  }
  return fallback;
};

const pricingSummary = (
  provider: Provider | undefined,
  modelId: string | undefined,
  durationSeconds: number,
  resolution: string,
  audio: boolean,
) => {
  if (!provider || !modelId) return null;
  let rows = flattenPricing(provider.pricing.models).filter(
    (entry) => entry.modelId === modelId,
  );
  if (!rows.length) return null;
  const exactVariant = `${resolution}:${String(audio)}`;
  const exactRows = rows.filter((entry) => entry.variant === exactVariant);
  if (exactRows.length) {
    rows = exactRows;
  } else if (resolution) {
    const resolutionRows = rows.filter((entry) =>
      entry.variant.includes(resolution),
    );
    if (resolutionRows.length) rows = resolutionRows;
  }
  if (rows.some((entry) => entry.row.unit === "internal")) {
    return "Self-hosted GPU/runtime cost";
  }
  if (rows.some((entry) => entry.row.unit === "video_token")) {
    return "Usage-based video-token pricing";
  }

  const prices = rows
    .map(({ row }) => {
      const unitPrice = Number(row.price_usd);
      if (!Number.isFinite(unitPrice)) return null;
      if (row.unit === "second") return unitPrice * durationSeconds;
      if (row.unit === "generation" && row.duration_seconds) {
        return unitPrice * Math.ceil(durationSeconds / row.duration_seconds);
      }
      return unitPrice;
    })
    .filter((value): value is number => value != null);
  if (!prices.length) return null;
  const minimum = Math.min(...prices);
  const maximum = Math.max(...prices);
  const price =
    minimum === maximum
      ? `$${minimum.toFixed(2)}`
      : `$${minimum.toFixed(2)}-$${maximum.toFixed(2)}`;
  const perSecond = rows.every((entry) => entry.row.unit === "second");
  return perSecond
    ? `Catalog estimate ${price} for ${durationSeconds}s`
    : `Catalog estimate ${price} for ${durationSeconds}s`;
};

export const LabsPage = () => {
  const {
    registry,
    source: registrySource,
    isLoading: registryLoading,
    shadow: registryShadow = {
      status: "unavailable",
      catalog_digest: null,
      differences: [],
    },
  } = useI2vRegistry();
  const queryClient = useQueryClient();
  const providers = useMemo(
    () =>
      registry.providers.filter(
        (provider) =>
          provider.capabilities.supports_image_to_video &&
          !provider.virtual_route &&
          provider.id !== "wan_comfy" &&
          (provider.labs?.available || provider.id === "kling_direct"),
      ),
    [registry.providers],
  );
  const [providerId, setProviderId] = useState("");
  const [requiresConcreteProviderChoice, setRequiresConcreteProviderChoice] =
    useState(false);
  const provider =
    providers.find((item) => item.id === providerId) ??
    (requiresConcreteProviderChoice ? undefined : providers[0]);
  const [modelId, setModelId] = useState("");
  const model: ModelVariant | undefined =
    provider?.models?.find((item) => item.id === modelId) ??
    provider?.models?.[0];
  const capabilities = useMemo(
    () =>
      operationCapabilities(
        model,
        model?.capabilities ?? provider?.capabilities,
      ),
    [model, provider?.capabilities],
  );
  const [prompt, setPrompt] = useState(
    "Cinematic natural motion, stable identity and geometry, realistic lighting, controlled camera movement.",
  );
  const [negativePrompt, setNegativePrompt] = useState("");
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [resolution, setResolution] = useState("");
  const [seed, setSeed] = useState("");
  const [generateAudio, setGenerateAudio] = useState(false);
  const [workflowId, setWorkflowId] = useState("");
  const [loraId, setLoraId] = useState("");
  const [loraStrength, setLoraStrength] = useState("0");
  const [firstFrame, setFirstFrame] = useState<File | null>(null);
  const [lastFrame, setLastFrame] = useState<File | null>(null);
  const [referenceImages, setReferenceImages] = useState<File[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<"create" | "run">("create");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [budgetError, setBudgetError] =
    useState<ReturnType<typeof labBudgetExceeded>>(null);
  const firstFrameUrl = useObjectUrl(firstFrame);
  const availableResolutions = useMemo(
    () =>
      constrainedResolutions(
        model,
        capabilities?.supported_resolutions ?? [],
        generateAudio,
      ),
    [model, capabilities?.supported_resolutions, generateAudio],
  );

  useEffect(() => {
    if (!providerId && providers[0] && !requiresConcreteProviderChoice) {
      setProviderId(
        providers.find((item) => item.deployment?.runnable)?.id ??
          providers[0].id,
      );
    }
  }, [providerId, providers, requiresConcreteProviderChoice]);

  useEffect(() => {
    if (!provider || !capabilities) return;
    const defaultModel =
      provider.models?.find(
        (item) => item.id === provider.deployment?.deployed_model_id,
      ) ??
      provider.models?.find((item) => item.deployment?.runnable) ??
      provider.models?.[0];
    if (!modelId || !provider.models?.some((item) => item.id === modelId)) {
      setModelId(defaultModel?.id ?? "");
      return;
    }
    const durations = durationOptions(provider, capabilities);
    setDuration(durations.includes(duration) ? duration : durations[0]);
    const explicitAspect =
      (capabilities.aspect_ratio_control ?? "explicit") === "explicit";
    const nextAspect = explicitAspect
      ? capabilities.supported_aspect_ratios.includes(aspectRatio)
        ? aspectRatio
        : firstOption(capabilities.supported_aspect_ratios, "16:9")
      : "";
    setAspectRatio(nextAspect);
    const outputPresets = provider.labs?.output_presets ?? [];
    const matchingPresets = outputPresets.filter(
      (preset) => preset.aspect_ratio === nextAspect,
    );
    const resolutions = matchingPresets.length
      ? matchingPresets.map((preset) => preset.id)
      : availableResolutions;
    setResolution(
      resolutions.includes(resolution) ? resolution : firstOption(resolutions),
    );
    if (!capabilities.supports_first_last_frame) setLastFrame(null);
    if (!capabilities.supports_reference_images && referenceImages.length) {
      setReferenceImages([]);
    } else if (referenceImages.length > capabilities.max_reference_images) {
      setReferenceImages(
        referenceImages.slice(0, capabilities.max_reference_images),
      );
    }
    if (!capabilities.supports_native_audio) setGenerateAudio(false);
    if (!capabilities.supports_negative_prompt) setNegativePrompt("");
    if (!capabilities.supports_seed) setSeed("");
    if (provider.id === "ltx_comfy") {
      setWorkflowId(
        provider.labs?.workflow_variants.some((item) => item.id === workflowId)
          ? workflowId
          : (provider.labs?.workflow_variants[0]?.id ?? ""),
      );
      const selectedLora = provider.labs?.lora_variants.find(
        (item) => item.id === loraId,
      );
      if (!selectedLora) {
        const nextLora = provider.labs?.lora_variants[0];
        setLoraId(nextLora?.id ?? "");
        setLoraStrength(String(nextLora?.default_strength ?? 0));
      }
    } else {
      setWorkflowId("");
      setLoraId("");
      setLoraStrength("0");
    }
  }, [
    provider,
    capabilities,
    modelId,
    duration,
    aspectRatio,
    resolution,
    referenceImages,
    workflowId,
    loraId,
    availableResolutions,
    generateAudio,
  ]);

  const jobsQuery = useQuery({
    queryKey: ["labs-jobs"],
    queryFn: listLabJobs,
    refetchInterval: (query) =>
      query.state.data?.data.some((job) => isActiveLabStatus(job.status))
        ? 4000
        : false,
  });
  const detailQuery = useQuery({
    queryKey: ["labs-job", selectedId],
    queryFn: () => getLabJob(selectedId!),
    enabled: Boolean(selectedId),
    refetchInterval: (query) =>
      query.state.data && isActiveLabStatus(query.state.data.status)
        ? 3000
        : false,
  });
  const selected = detailQuery.data;
  const registryReady =
    registrySource === "live" &&
    registry.deployment?.status === "ready" &&
    provider?.deployment?.runnable === true &&
    model?.deployment?.runnable === true &&
    !requiresConcreteProviderChoice;
  const outputPresets = (provider?.labs?.output_presets ?? []).filter(
    (preset) => preset.aspect_ratio === aspectRatio,
  );
  const selectedLora = provider?.labs?.lora_variants.find(
    (item) => item.id === loraId,
  );

  const submit = async () => {
    if (!provider || !model || !registryReady || !firstFrame || !prompt.trim())
      return;
    setSubmitting(true);
    setSubmitError(null);
    setBudgetError(null);
    try {
      const first = await uploadLabImage(firstFrame, "first_frame");
      const last = lastFrame
        ? await uploadLabImage(lastFrame, "last_frame")
        : null;
      const references = await Promise.all(
        referenceImages.map((file) => uploadLabImage(file, "reference")),
      );
      const created = await createLabJob({
        idempotency_key: crypto.randomUUID(),
        mode: "image_to_video",
        provider_id: provider.id,
        model_id: model.id,
        prompt: prompt.trim(),
        negative_prompt: negativePrompt.trim() || undefined,
        duration_seconds: duration,
        aspect_ratio: aspectRatio || undefined,
        resolution: resolution || undefined,
        generate_audio: generateAudio,
        seed: seed ? Number(seed) : undefined,
        registry_digest: registry.registry_digest,
        provider_options: providerOptions(
          provider.id,
          workflowId,
          loraId,
          loraStrength,
        ),
        inputs: {
          first_frame: first,
          last_frame: last,
          reference_images: references,
        },
      });
      setSelectedId(created.job_id);
      setWorkspace("run");
      await queryClient.invalidateQueries({ queryKey: ["labs-jobs"] });
    } catch (error) {
      const budgetExceeded = labBudgetExceeded(error);
      if (budgetExceeded) {
        setBudgetError(budgetExceeded);
      } else {
        setSubmitError(
          error instanceof Error ? error.message : "Labs submission failed",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    if (!selected) return;
    setSubmitError(null);
    setBudgetError(null);
    try {
      await cancelLabJob(selected.job_id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["labs-jobs"] }),
        queryClient.invalidateQueries({
          queryKey: ["labs-job", selected.job_id],
        }),
      ]);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Could not cancel run",
      );
    }
  };

  const reuseSettings = (job: LabJob) => {
    const request = job.request;
    if (!request) return;
    const identity = resolveLabProviderIdentity(job);
    const reusableProviderId =
      identity.concreteProviderId ??
      (providers.some((item) => item.id === request.provider_id)
        ? request.provider_id
        : "");
    setProviderId(reusableProviderId);
    setRequiresConcreteProviderChoice(!reusableProviderId);
    setBudgetError(null);
    setSubmitError(
      reusableProviderId
        ? null
        : "This legacy Kling run has no proven concrete route. Choose PiAPI or Direct and revalidate the recipe before submitting.",
    );
    setModelId(request.model_id ?? "");
    setPrompt(request.prompt);
    setNegativePrompt(request.negative_prompt ?? "");
    setDuration(request.duration_seconds);
    setAspectRatio(request.aspect_ratio ?? "16:9");
    setResolution(request.resolution ?? "");
    setSeed(request.seed == null ? "" : String(request.seed));
    setGenerateAudio(request.generate_audio);
    const ltxOptions =
      request.provider_options?.provider_id === "ltx_comfy"
        ? request.provider_options
        : null;
    setWorkflowId(ltxOptions?.workflow_id ?? "");
    setLoraId(ltxOptions?.lora_id ?? "");
    setLoraStrength(
      ltxOptions?.lora_strength == null
        ? "0"
        : String(ltxOptions.lora_strength),
    );
    setWorkspace("create");
  };

  const stageAspect = (
    (workspace === "run" ? selected?.aspect_ratio : aspectRatio) || "16:9"
  ).replace(":", " / ");
  const selectedInputPreview =
    selected?.thumbnail_url ??
    selected?.request?.inputs.first_frame.url ??
    selected?.inputs.first_frame.url;
  const currentPricing = pricingSummary(
    provider,
    model?.id,
    duration,
    resolution,
    generateAudio,
  );
  const selectedProviderLabel =
    provider?.id === "kling_piapi" || provider?.id === "kling_direct"
      ? concreteKlingProviderLabel(provider.id)
      : provider?.display_name;
  return (
    <div className="min-h-[calc(100vh-3rem)] bg-background p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FlaskConical className="size-5" />
            <h1 className="text-2xl font-semibold">Labs</h1>
            <Badge variant={registrySource === "live" ? "default" : "outline"}>
              {registrySource === "live"
                ? "Live registry"
                : "Registry snapshot"}
            </Badge>
            <Badge
              variant={
                registryShadow.status === "match" ? "secondary" : "outline"
              }
              title={
                registryShadow.status === "drift"
                  ? `${registryShadow.differences.length} v3/v4 parity differences`
                  : "Registry v4 is evaluated in read-only shadow"
              }
            >
              {registryShadow.status === "match"
                ? "v4 shadow match"
                : registryShadow.status === "drift"
                  ? `v4 drift · ${registryShadow.differences.length}`
                  : "v4 shadow unavailable"}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Persistent provider experiments isolated from WhatsApp Echo routing.
          </p>
        </div>
        <Badge variant="secondary" className="gap-1.5">
          <CloudCog className="size-3.5" /> AWS queue + storage
        </Badge>
      </div>

      <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)_340px]">
        <Card>
          <CardContent className="p-4">
            <Tabs defaultValue="create">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="create">Create Video</TabsTrigger>
                <TabsTrigger value="text">Text to Video</TabsTrigger>
              </TabsList>
              <TabsContent value="create" className="mt-4 space-y-4">
                <FrameInput
                  id="labs-first-frame"
                  label="First frame"
                  file={firstFrame}
                  onChange={(file) => {
                    setFirstFrame(file);
                    setWorkspace("create");
                  }}
                  required
                />
                {capabilities?.supports_first_last_frame ? (
                  <FrameInput
                    id="labs-last-frame"
                    label="Last frame"
                    file={lastFrame}
                    onChange={(file) => {
                      setLastFrame(file);
                      if (file) setReferenceImages([]);
                      setWorkspace("create");
                    }}
                  />
                ) : null}
                {capabilities?.supports_reference_images &&
                capabilities.max_reference_images > 0 ? (
                  <ReferenceImagesInput
                    id="labs-reference-images"
                    files={referenceImages}
                    maxFiles={capabilities.max_reference_images}
                    onChange={(files) => {
                      setReferenceImages(files);
                      if (files.length) setLastFrame(null);
                      setWorkspace("create");
                    }}
                  />
                ) : null}
                <div className="space-y-2">
                  <Label htmlFor="labs-prompt">Prompt</Label>
                  <Textarea
                    id="labs-prompt"
                    value={prompt}
                    onChange={(event) => {
                      setPrompt(event.target.value);
                      setWorkspace("create");
                    }}
                    rows={6}
                    maxLength={6000}
                  />
                </div>
                {capabilities?.supports_negative_prompt ? (
                  <div className="space-y-2">
                    <Label htmlFor="labs-negative-prompt">
                      Negative prompt
                    </Label>
                    <Textarea
                      id="labs-negative-prompt"
                      value={negativePrompt}
                      onChange={(event) => {
                        setNegativePrompt(event.target.value);
                        setWorkspace("create");
                      }}
                      rows={3}
                      maxLength={6000}
                      placeholder="Artifacts or changes to avoid"
                    />
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select
                    value={provider?.id ?? ""}
                    onValueChange={(value) => {
                      setProviderId(value);
                      setRequiresConcreteProviderChoice(false);
                      setSubmitError(null);
                      setBudgetError(null);
                      setWorkspace("create");
                    }}
                  >
                    <SelectTrigger className="w-full" aria-label="Provider">
                      <SelectValue placeholder="Select a provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.display_name}
                          {!item.deployment?.runnable ? " · unavailable" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {provider?.models?.length ? (
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Select
                        value={model?.id ?? ""}
                        onValueChange={(value) => {
                          setModelId(value);
                          setWorkspace("create");
                        }}
                      >
                        <SelectTrigger className="w-full" aria-label="Model">
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent>
                          {provider.models.map((item) => (
                            <SelectItem
                              key={item.id}
                              value={item.id}
                              disabled={!item.deployment?.runnable}
                            >
                              {item.display_name}
                              {!item.deployment?.runnable
                                ? " · unavailable"
                                : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="font-mono text-[11px] text-muted-foreground">
                        {model?.id}
                      </p>
                      {selectedProviderLabel ? (
                        <Badge variant="outline">{selectedProviderLabel}</Badge>
                      ) : null}
                    </div>
                  ) : null}
                  {currentPricing ? (
                    <p className="text-xs text-muted-foreground">
                      {currentPricing}
                    </p>
                  ) : null}
                  {provider?.deployment?.runnable === false ? (
                    <Alert variant="destructive">
                      <TriangleAlert className="size-4" />
                      <AlertTitle>
                        {selectedProviderLabel ?? "Provider"} unavailable
                        {provider.deployment.readiness?.state
                          ? ` · ${provider.deployment.readiness.state.replaceAll("_", " ")}`
                          : ""}
                      </AlertTitle>
                      <AlertDescription>
                        {provider.deployment.reasons.join(" ")}
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {provider && model ? (
                    <ReadinessDiagnostics
                      providerId={provider.id}
                      modelId={model.id}
                      readiness={provider.deployment?.readiness}
                    />
                  ) : null}
                </div>
                {provider ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label>Duration</Label>
                      <Select
                        value={String(duration)}
                        onValueChange={(value) => {
                          setDuration(Number(value));
                          setWorkspace("create");
                        }}
                      >
                        <SelectTrigger className="w-full" aria-label="Duration">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {durationOptions(provider, capabilities!).map(
                            (value) => (
                              <SelectItem key={value} value={String(value)}>
                                {value}s
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Aspect</Label>
                      {(capabilities?.aspect_ratio_control ?? "explicit") ===
                      "explicit" ? (
                        <Select
                          value={aspectRatio}
                          onValueChange={(value) => {
                            setAspectRatio(value);
                            setWorkspace("create");
                          }}
                        >
                          <SelectTrigger
                            className="w-full"
                            aria-label="Aspect ratio"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {capabilities?.supported_aspect_ratios.map(
                              (value) => (
                                <SelectItem key={value} value={value}>
                                  {value}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={
                            capabilities?.aspect_ratio_control === "source"
                              ? "Follows input"
                              : "Deployment fixed"
                          }
                          aria-label="Aspect ratio behavior"
                          disabled
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Resolution</Label>
                      {outputPresets.length ? (
                        <Select
                          value={resolution}
                          onValueChange={(value) => {
                            setResolution(value);
                            setWorkspace("create");
                          }}
                        >
                          <SelectTrigger
                            className="w-full"
                            aria-label="Resolution preset"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {outputPresets.map((preset) => (
                              <SelectItem key={preset.id} value={preset.id}>
                                {preset.display_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : availableResolutions.length ? (
                        <Select
                          value={resolution}
                          onValueChange={(value) => {
                            setResolution(value);
                            setWorkspace("create");
                          }}
                        >
                          <SelectTrigger
                            className="w-full"
                            aria-label="Resolution"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableResolutions.map((value) => (
                              <SelectItem key={value} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value="Native"
                          aria-label="Resolution"
                          disabled
                        />
                      )}
                    </div>
                  </div>
                ) : null}
                <div className="flex items-end gap-3">
                  {capabilities?.supports_seed ? (
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="labs-seed">Seed</Label>
                      <Input
                        id="labs-seed"
                        type="number"
                        min={0}
                        value={seed}
                        onChange={(event) => {
                          setSeed(event.target.value);
                          setWorkspace("create");
                        }}
                        placeholder="Random"
                      />
                    </div>
                  ) : null}
                  {capabilities?.supports_native_audio ? (
                    <div className="flex items-center gap-2 pb-2">
                      <Switch
                        id="labs-audio"
                        checked={generateAudio}
                        onCheckedChange={(checked) => {
                          setGenerateAudio(checked);
                          setWorkspace("create");
                        }}
                      />
                      <Label htmlFor="labs-audio">Native audio</Label>
                    </div>
                  ) : null}
                </div>
                {provider?.id === "ltx_comfy" ? (
                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="space-y-2">
                      <Label>LTX workflow</Label>
                      <Select
                        value={workflowId}
                        onValueChange={(value) => {
                          setWorkflowId(value);
                          setWorkspace("create");
                        }}
                      >
                        <SelectTrigger
                          className="w-full"
                          aria-label="LTX workflow"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {provider.labs?.workflow_variants.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
                      <div className="space-y-2">
                        <Label>Identity LoRA</Label>
                        <Select
                          value={loraId}
                          onValueChange={(value) => {
                            setLoraId(value);
                            const next = provider.labs?.lora_variants.find(
                              (item) => item.id === value,
                            );
                            setLoraStrength(
                              String(next?.default_strength ?? 0),
                            );
                            setWorkspace("create");
                          }}
                        >
                          <SelectTrigger
                            className="w-full"
                            aria-label="LTX identity LoRA"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {provider.labs?.lora_variants.map((item) => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.display_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="labs-lora-strength">Strength</Label>
                        <Input
                          id="labs-lora-strength"
                          type="number"
                          min={0}
                          max={selectedLora?.maximum_strength ?? 0}
                          step={0.05}
                          value={loraStrength}
                          disabled={!selectedLora?.runtime_value}
                          onChange={(event) => {
                            setLoraStrength(event.target.value);
                            setWorkspace("create");
                          }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedLora?.trigger_phrase
                        ? `Prompt trigger: “${selectedLora.trigger_phrase}”. The fixed LTX distilled acceleration adapter remains stacked underneath.`
                        : "No character adapter is applied. For Jason, upload a Jason first frame; the existing Jason LoRAs target still-image models and are not LTX-compatible."}
                    </p>
                  </div>
                ) : null}
                {registryShadow.status === "drift" ? (
                  <Alert>
                    <TriangleAlert className="size-4" />
                    <AlertTitle>Registry v4 shadow drift</AlertTitle>
                    <AlertDescription>
                      {registryShadow.differences.length} catalog decision
                      {registryShadow.differences.length === 1
                        ? " differs"
                        : "s differ"}{" "}
                      from the current v3 reader. Generation still uses v3 and
                      remains fail-closed; operators can inspect the first code:{" "}
                      {registryShadow.differences[0]?.code}.
                    </AlertDescription>
                  </Alert>
                ) : null}
                {provider?.id === "ltx_comfy" || provider?.id === "h3_comfy" ? (
                  <Alert>
                    <CloudCog className="size-4" />
                    <AlertTitle>Self-hosted GPU</AlertTitle>
                    <AlertDescription>
                      A cold run can spend several minutes starting and
                      hydrating the worker.
                    </AlertDescription>
                  </Alert>
                ) : null}
                {!registryReady ? (
                  <Alert variant="destructive">
                    <TriangleAlert className="size-4" />
                    <AlertTitle>Generation is fail-closed</AlertTitle>
                    <AlertDescription>
                      {registrySource !== "live"
                        ? "The live registry is unavailable; snapshots are read-only."
                        : registry.deployment?.status !== "ready"
                          ? "The catalog does not match the pinned composer deployment."
                          : (model?.deployment?.reasons[0] ??
                            provider?.deployment?.reasons[0] ??
                            "This provider/model is not configured in this environment.")}
                    </AlertDescription>
                  </Alert>
                ) : null}
                {submitError ? (
                  <Alert variant="destructive">
                    <TriangleAlert className="size-4" />
                    <AlertTitle>Could not create run</AlertTitle>
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                ) : null}
                {budgetError ? <LabBudgetAlert error={budgetError} /> : null}
                <Button
                  className="w-full"
                  size="lg"
                  disabled={
                    submitting ||
                    registryLoading ||
                    !registryReady ||
                    !provider ||
                    !model ||
                    !firstFrame ||
                    !prompt.trim()
                  }
                  onClick={() => void submit()}
                >
                  {submitting ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  {submitting ? "Uploading & queueing…" : "Generate"}
                </Button>
              </TabsContent>
              <TabsContent value="text">
                <Alert>
                  <Film className="size-4" />
                  <AlertTitle>Capability not deployed</AlertTitle>
                  <AlertDescription>
                    Text-to-video will unlock when a real T2V provider contract
                    is exported.
                  </AlertDescription>
                </Alert>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Card className="min-h-[680px] overflow-hidden">
          <CardHeader className="gap-3 border-b">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">
                {workspace === "create"
                  ? "Draft preview"
                  : selected
                    ? `${selected.provider_id} run`
                    : "Run details"}
              </CardTitle>
              {workspace === "run" && selected ? (
                <Badge variant="secondary">{selected.status}</Badge>
              ) : (
                <Badge variant="outline">Draft</Badge>
              )}
            </div>
            <Tabs
              value={workspace}
              onValueChange={(value) =>
                setWorkspace(value === "run" ? "run" : "create")
              }
            >
              <TabsList className="grid w-full max-w-sm grid-cols-2">
                <TabsTrigger value="create">Create</TabsTrigger>
                <TabsTrigger value="run" disabled={!selectedId}>
                  Run details
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="space-y-4 p-4 lg:p-6">
            <div className="grid min-h-[440px] place-items-center rounded-xl bg-muted/40 p-4">
              <div
                className="relative grid max-h-[560px] w-full max-w-3xl place-items-center overflow-hidden rounded-lg bg-black"
                style={{ aspectRatio: stageAspect }}
              >
                {workspace === "create" && firstFrameUrl ? (
                  <img
                    src={firstFrameUrl}
                    alt="Draft first frame preview"
                    className="size-full object-contain"
                  />
                ) : workspace === "create" ? (
                  <div className="text-center text-muted-foreground">
                    <Film className="mx-auto mb-3 size-8" />
                    <p className="text-sm font-medium">Your creation stage</p>
                    <p className="mt-1 text-xs">
                      Choose a first frame to preview the current draft.
                    </p>
                  </div>
                ) : detailQuery.isLoading ? (
                  <LoaderCircle className="size-7 animate-spin text-white" />
                ) : selected?.output_url ? (
                  <video
                    src={selected.output_url}
                    controls
                    className="size-full object-contain"
                  />
                ) : selectedInputPreview ? (
                  <img
                    src={selectedInputPreview}
                    alt="Input preview for selected run"
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Film className="mx-auto mb-3 size-8" />
                    <p className="text-sm font-medium">No output available</p>
                    <p className="mt-1 text-xs">
                      Select a run from History to inspect it.
                    </p>
                  </div>
                )}
                {workspace === "run" &&
                selected &&
                isActiveLabStatus(selected.status) ? (
                  <div className="absolute inset-0 grid place-items-center bg-black/60 text-white">
                    <div className="text-center">
                      <LoaderCircle className="mx-auto mb-3 size-7 animate-spin" />
                      <p className="font-medium capitalize">
                        {selected.status.replace("_", " ")}
                      </p>
                      <p className="mt-1 text-xs text-white/70">
                        You can leave this page; the run is persistent.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {workspace === "create" ? (
              <p className="text-xs text-muted-foreground">
                This preview is tied to the unsent draft. Opening a historical
                run will not replace the frame you are preparing.
              </p>
            ) : null}
            {workspace === "run" && selected ? (
              <>
                <div className="flex flex-wrap gap-2">
                  {selected.output_url ? (
                    <Button asChild size="sm">
                      <a
                        href={selected.output_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="size-4" /> Open video in new
                        tab
                      </a>
                    </Button>
                  ) : null}
                  {isActiveLabStatus(selected.status) ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => void cancel()}
                    >
                      <CircleStop className="size-4" /> Cancel
                    </Button>
                  ) : null}
                </div>
                <LabRunRecipe
                  job={selected}
                  onUseSettings={() => reuseSettings(selected)}
                />
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Run metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-2 text-xs sm:grid-cols-2">
                    <p>
                      <span className="text-muted-foreground">Job</span>
                      <span className="ml-2 break-all font-mono">
                        {selected.job_id}
                      </span>
                    </p>
                    <p>
                      <span className="text-muted-foreground">Created</span>
                      <span className="ml-2">
                        {new Date(selected.created_at).toLocaleString()}
                      </span>
                    </p>
                    {selected.result?.provider_task_id ? (
                      <p>
                        <span className="text-muted-foreground">
                          Provider task
                        </span>
                        <span className="ml-2 break-all font-mono">
                          {selected.result.provider_task_id}
                        </span>
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              </>
            ) : null}
            {workspace === "run" && selected?.result?.error ? (
              <Alert variant="destructive">
                <TriangleAlert className="size-4" />
                <AlertTitle>
                  {selected.result.error.code || "Generation failed"}
                </AlertTitle>
                <AlertDescription>
                  {selected.result.error.message}
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* Same gate as the /labs route itself (CRM.tsx) — stated here too so
              the card carries its own admin boundary if it is ever reused. */}
          <CanAccess resource="configuration" action="edit">
            <TestEchoLauncherCard />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <Suspense fallback={null}>
              <LikenessCard />
            </Suspense>
          </CanAccess>
          <LabHistory
            jobs={jobsQuery.data?.data ?? []}
            selectedId={selectedId}
            onSelect={(jobId) => {
              setSelectedId(jobId);
              setWorkspace("run");
            }}
            isLoading={jobsQuery.isLoading}
          />
        </div>
      </div>
    </div>
  );
};

LabsPage.path = "/labs";
